import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@akuna/ui";
import { Card, CardContent } from "@/components/ui/card";
import { useAcceptInvite, useCompleteInvite, type UserInvite } from "@/hooks/useInvites";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Building2, Mail, UserCheck, Sparkles, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import type { User } from "@supabase/supabase-js";

type ClinicInviteInfo = {
  id: string;
  name: string;
  customer?: { name?: string | null } | null;
};

export default function InviteAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const acceptInvite = useAcceptInvite();
  const completeInvite = useCompleteInvite();
  const { t, i18n } = useTranslation("auth");
  const [inviteData, setInviteData] = useState<UserInvite | null>(null);
  const [clinics, setClinics] = useState<ClinicInviteInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const loadInvite = async () => {
      const inviteToken = searchParams.get("inviteToken");
      if (!inviteToken) {
        toast({
          title: t("invite.invalidTitle"),
          description: t("invite.invalidDescription"),
          variant: "destructive",
        });
        navigate("/login");
        return;
      }

      // Check if user is already logged in
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      // Validar o convite
      acceptInvite.mutate(inviteToken, {
      onSuccess: async (invite) => {
        setInviteData(invite);
        
        // Buscar informações das clínicas
        const clinicIdsRaw = invite.clinic_ids;
        const clinicIdsParsed: string[] = Array.isArray(clinicIdsRaw) && clinicIdsRaw.length > 0
          ? clinicIdsRaw.filter((id): id is string => typeof id === "string")
          : [];
        
        const clinicIds: string[] = clinicIdsParsed.length > 0
          ? clinicIdsParsed
          : [invite.clinic_id];

        try {
          const { data: clinicsData } = await supabase
            .from("clinics")
            .select("id, name, customer:customers(name)")
            .in("id", clinicIds);

          if (clinicsData) {
            setClinics(clinicsData);
          }
        } catch (error) {
          console.error("Erro ao buscar clínicas:", error);
        }
        
        setLoading(false);
      },
      onError: (error: Error) => {
        toast({
          title: t("invite.invalidTitle"),
          description: error.message,
          variant: "destructive",
        });
        navigate("/login");
      },
    });
    };

    loadInvite();
  }, [searchParams, navigate, acceptInvite, toast, t]);

  const handleAcceptLoggedIn = async () => {
    if (!currentUser || !inviteData) return;

    const inviteToken = searchParams.get("inviteToken");
    if (!inviteToken) return;

    try {
      await completeInvite.mutateAsync({ token: inviteToken, userId: currentUser.id });
      
      const clinicNames = clinics.map((c) => c.name).join(", ");
      toast({
        title: t("invite.acceptedTitle"),
        description: clinics.length > 1
          ? t("invite.accessMultiple", { clinics: clinicNames })
          : t("invite.accessSingle", { clinics: clinicNames }),
      });
      
      navigate("/calendar");
    } catch (error: unknown) {
      toast({
        title: t("invite.acceptErrorTitle"),
        description: error instanceof Error ? error.message : t("invite.acceptErrorFallback"),
        variant: "destructive",
      });
    }
  };

  const handleAcceptNewUser = () => {
    const inviteToken = searchParams.get("inviteToken");
    navigate(`/signup?inviteToken=${inviteToken}`);
  };

  if (loading || acceptInvite.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">{t("loadingInvite")}</p>
        </div>
      </div>
    );
  }

  if (!inviteData) {
    return null;
  }

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: t("invite.roleLabels.admin"),
      veterinarian: t("invite.roleLabels.veterinarian"),
      receptionist: t("invite.roleLabels.receptionist"),
      user: t("invite.roleLabels.user"),
    };
    return labels[role] || role;
  };

  const customerName = clinics[0]?.customer?.name || "";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-2xl shadow-2xl border-primary/20">
        <CardContent className="p-0">
          {/* Header com gradiente */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground p-8 rounded-t-lg">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-white/20 p-4 rounded-full">
                <Sparkles className="h-12 w-12" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-center mb-2">
              {t("invite.headerTitle")}
            </h1>
            <p className="text-center text-primary-foreground/90">
              {t("invite.headerSubtitle")}
            </p>
          </div>

          {/* Conteúdo */}
          <div className="p-8 space-y-6">
            {/* Informações do convite */}
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <Mail className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">{t("invite.invitedEmail")}</p>
                  <p className="text-sm text-muted-foreground">{inviteData.email}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <UserCheck className="h-5 w-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">{t("invite.role")}</p>
                  <p className="text-sm text-muted-foreground">{getRoleLabel(inviteData.role)}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
                <Building2 className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium mb-2">
                    {t(`invite.clinic_${clinics.length === 1 ? "one" : "other"}`)}
                  </p>
                  <div className="space-y-2">
                    {clinics.map((clinic) => (
                      <div
                        key={clinic.id}
                        className="flex items-center gap-2 text-sm"
                      >
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="font-medium">{clinic.name}</span>
                      </div>
                    ))}
                  </div>
                  {customerName && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {t("invite.organization", { name: customerName })}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Benefícios */}
            <div className="border-t pt-6">
              <p className="text-sm text-muted-foreground mb-4">
                {t("invite.benefitsTitle")}
              </p>
              <ul className="space-y-2 text-sm">
                {(t("invite.benefits", { returnObjects: true }) as string[]).map((benefit) => (
                  <li key={benefit} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Ações */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              {currentUser ? (
                <Button
                  onClick={handleAcceptLoggedIn}
                  size="lg"
                  className="flex-1 text-lg font-semibold"
                  disabled={completeInvite.isPending}
                >
                  {completeInvite.isPending ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-5 w-5" />
                  )}
                  {t("invite.acceptLoggedIn")}
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleAcceptNewUser}
                    size="lg"
                    className="flex-1 text-lg font-semibold"
                  >
                    <Sparkles className="mr-2 h-5 w-5" />
                    {t("invite.acceptCreateAccount")}
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    asChild
                    className="sm:w-auto"
                  >
                    <Link to={`/login?inviteToken=${searchParams.get("inviteToken")}`}>{t("invite.alreadyHaveAccount")}</Link>
                  </Button>
                </>
              )}
            </div>

            {/* Nota de expiração */}
            <p className="text-xs text-center text-muted-foreground">
              {t("invite.expiresAt", {
                date: new Date(inviteData.expires_at).toLocaleDateString(i18n.language === "en" ? "en-US" : "pt-BR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                }),
              })}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
